import {
  AutoResponseMessage,
  HighPrioritySystemMessage,
  LowPrioritySystemMessage,
  MESSAGE_TYPES,
} from '@zg-rentals/satellite-components';
import { Anchor, List } from '@zillow/constellation-10';
import Link from 'next/link';
import type { MouseEvent, ReactNode } from 'react';
import { memo, useCallback, useMemo } from 'react';
import useInternalHref from '../../hooks/useInternalHref';
import { SatelliteAiAutoResponseType } from '../../types/GraphqlTypes';
import type { ConversationMessage } from '../../utils/conversationMessageUtils';
import { TRIGGER_LOCATION, type TriggerLocation } from '../../constants/clickstreamEvents/globals';
import type {
  ConversationIdentity,
  HandleOpenDeleteAttachmentsModal,
  HandleOpenImagesViewer,
  OnResendMessageComplete,
} from '../MessageListItems';
import ApplicationRequest from './components/ApplicationRequest';
import ListingMessage from './components/ListingMessage';
import PhoneLead from './components/PhoneLead';

const { LISTING_MESSAGE, PHONE_LEAD, RENTER_PROFILE_SUBMISSION, SYSTEM_MESSAGE } = MESSAGE_TYPES;
const { APPLICATION_REQUEST } = SYSTEM_MESSAGE.APPLICATIONS;

export type OnSystemMessageCtaClickArgs = {
  messageType: string;
  url: string;
};

export type OnSystemMessageCtaClick = (args: OnSystemMessageCtaClickArgs) => void;

type MessageItemProps = {
  conversation: ConversationIdentity;
  leadId?: string | null;
  onResendMessageComplete?: OnResendMessageComplete;
  isLastMessageInList: boolean;
  message: ConversationMessage;
  onSystemMessageCtaClick: OnSystemMessageCtaClick;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
  triggerLocation: TriggerLocation;
};

const MessageItem = ({
  conversation,
  leadId,
  onResendMessageComplete,
  isLastMessageInList,
  message: messageObject,
  onSystemMessageCtaClick,
  openDeleteAttachmentsModal,
  openImagesViewer,
  triggerLocation,
}: MessageItemProps): JSX.Element => {
  const { conversationId, listingAlias = '', clientTimeZone } = conversation;
  const {
    aiAutoResponseType,
    attachments,
    isMessageOwner,
    message: messageContent,
    messageDateMs,
    messageLink,
    messageType,
    senderName,
    title,
    uiTreatmentType,
  } = messageObject;

  const ctaText = messageLink?.text ?? '';
  const systemMessageUrl = messageLink?.url ?? '';
  const isHighPrioritySystemMessage =
    uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY || uiTreatmentType === MESSAGE_TYPES.HIGH_PRIORITY_WITH_LOGO;
  const shouldDisplaySentStatus = !!messageDateMs && isLastMessageInList && !!isMessageOwner;

  const handleSystemMessageCtaClick = useCallback(
    (event?: MouseEvent<HTMLAnchorElement>) => {
      event?.preventDefault();
      onSystemMessageCtaClick({ messageType, url: systemMessageUrl });
    },
    [onSystemMessageCtaClick, messageType, systemMessageUrl],
  );

  const { internalHref } = useInternalHref(systemMessageUrl);

  const hasSpecialClickHandler =
    messageType === RENTER_PROFILE_SUBMISSION || triggerLocation === TRIGGER_LOCATION.LeadManagementPage;

  const renderInAppLink = useMemo(() => {
    if (!internalHref || hasSpecialClickHandler) return undefined;
    return (children: ReactNode) => (
      <Anchor asChild css={{ whiteSpace: 'nowrap' }}>
        <Link href={internalHref}>{children}</Link>
      </Anchor>
    );
  }, [internalHref, hasSpecialClickHandler]);

  let content: JSX.Element;
  if (aiAutoResponseType === SatelliteAiAutoResponseType.EliseAi && messageContent) {
    content = <AutoResponseMessage message={messageContent} messageDateMs={messageDateMs} metaLabelType="eliseAI" />;
  } else if (aiAutoResponseType === SatelliteAiAutoResponseType.ValAi && messageContent) {
    content = <AutoResponseMessage message={messageContent} messageDateMs={messageDateMs} metaLabelType="valAI" />;
  } else {
    switch (messageType) {
      case LISTING_MESSAGE:
        content = (
          <ListingMessage
            attachments={attachments}
            conversation={conversation}
            leadId={leadId}
            onResendMessageComplete={onResendMessageComplete}
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
            senderName={senderName ?? ''}
            shouldDisplaySentStatus={shouldDisplaySentStatus}
            messageId={messageObject.messageId ?? ''}
            uiTreatmentType={uiTreatmentType ?? ''}
            openDeleteAttachmentsModal={openDeleteAttachmentsModal}
            openImagesViewer={openImagesViewer}
            triggerLocation={triggerLocation}
          />
        );
        break;
      case PHONE_LEAD:
        content = (
          <PhoneLead
            isMessageOwner={isMessageOwner ?? false}
            messageContent={messageContent ?? ''}
            messageDateMs={messageDateMs}
          />
        );
        break;
      case APPLICATION_REQUEST:
        content = (
          <ApplicationRequest
            conversationId={conversationId}
            clientTimeZone={clientTimeZone}
            listingAlias={listingAlias}
            messageObject={messageObject}
          />
        );
        break;
      default:
        content = isHighPrioritySystemMessage ? (
          <HighPrioritySystemMessage
            ctaText={ctaText}
            message={messageContent ?? ''}
            messageDateMs={messageDateMs}
            messageType={messageType}
            onCtaClick={renderInAppLink ? undefined : handleSystemMessageCtaClick}
            renderLink={renderInAppLink}
            title={title ?? ''}
            uiTreatmentType={uiTreatmentType ?? ''}
          />
        ) : (
          <LowPrioritySystemMessage
            ctaText={ctaText}
            message={messageContent ?? ''}
            messageDateMs={messageDateMs}
            onCtaClick={renderInAppLink ? undefined : handleSystemMessageCtaClick}
            renderLink={renderInAppLink}
            url={systemMessageUrl}
          />
        );
    }
  }

  return (
    <List.Item
      aria-live={isLastMessageInList ? 'polite' : 'off'}
      aria-roledescription="message"
      data-testid="message-item"
    >
      {content}
    </List.Item>
  );
};

export default memo(MessageItem);
